import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'

import {
  useGetAccountLifecycleCompetitorsQuery,
  useGetAccountLifecycleReasonsQuery,
  useGetAccountLifecycleSettingsQuery,
} from 'api/account-lifecycle/accountLifecycle'
import LifecycleCaptureFields from 'components/common/account-lifecycle/LifecycleCaptureFields'
import useLifecycleReasonOptions from 'components/common/account-lifecycle/useLifecycleReasonOptions'
import { HHAlert } from 'components/common/HHAlert'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import { selectIsAccountLifecycleCaptureEnabled } from 'data/tenant/tenantSettingsSelectors'
import T from 'T'
import { handleErrorWithFetchError } from 'utils/error'

import type { LifecycleCaptureValues } from 'components/common/account-lifecycle/LifecycleCaptureFields'
import type { AccountLifecycleCompetitorApi, AccountLifecycleReasonApi } from 'types/api/account-lifecycle/accountLifecycle'
import type { DeactivationCaptureApi } from 'types/api/account/accountDeactivation'

type DeactivateAccountDialogProps = {
  isOpen: boolean
  // Out of sight while a following step asks (the container drawer), keeping what was typed for its answer.
  isHidden?: boolean
  accountName: string
  accountNumber?: string
  runsInBackground: boolean
  isLoading: boolean
  onClose: () => void
  // Rejects with the request's error, so a field the server refused can be marked here.
  onConfirm: (capture: DeactivationCaptureApi) => Promise<unknown>
}

type ServerError = { data?: { exceptionType?: string; message?: string } }

const INITIAL_VALUES: LifecycleCaptureValues = { reasonId: '', competitorId: '', notes: '' }
const NO_REASONS: AccountLifecycleReasonApi[] = []
const NO_COMPETITORS: AccountLifecycleCompetitorApi[] = []

const DeactivateAccountDialog = ({
  isOpen,
  isHidden = false,
  accountName,
  accountNumber,
  runsInBackground,
  isLoading,
  onClose,
  onConfirm,
}: DeactivateAccountDialogProps) => {
  const isCaptureEnabled = useSelector(selectIsAccountLifecycleCaptureEnabled)
  const skip = !isOpen || !isCaptureEnabled
  const { data: reasonData, isError: hasReasonsError } = useGetAccountLifecycleReasonsQuery({ type: 'LOSS' }, { skip })
  const { data: competitorData, isError: hasCompetitorsError } = useGetAccountLifecycleCompetitorsQuery(undefined, { skip })
  const {
    data: settings,
    isFetching: isFetchingSettings,
    isError: hasSettingsError,
  } = useGetAccountLifecycleSettingsQuery(undefined, { skip })
  const {
    control,
    handleSubmit,
    reset,
    resetField,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<LifecycleCaptureValues>({ defaultValues: INITIAL_VALUES })
  const lossReasonId = useWatch({ control, name: 'reasonId' })
  const lossCompetitorId = useWatch({ control, name: 'competitorId' })

  const reasons = reasonData?.reasons ?? NO_REASONS
  const requireLossReason = isCaptureEnabled && Boolean(settings?.requireLossReasonOnDeactivate)
  const { reasonOptions, competitorOptions, showCompetitor } = useLifecycleReasonOptions({
    reasons,
    competitors: competitorData?.competitors ?? NO_COMPETITORS,
    isReasonRequired: requireLossReason,
    selectedReasonId: lossReasonId,
  })
  // With capture on, waits for the settings (they decide whether a reason is required) and for a refused field to change.
  const isCaptureReady = !isCaptureEnabled || (Boolean(settings) && !isFetchingSettings && !hasSettingsError)
  const canConfirm =
    isCaptureReady &&
    (!requireLossReason || Boolean(lossReasonId)) &&
    (!showCompetitor || Boolean(lossCompetitorId)) &&
    !errors.reasonId &&
    !errors.competitorId
  // A required reason with none set up can never be met here, so the field says where to fix it.
  const hasNoReasonToPick = requireLossReason && Boolean(reasonData) && reasons.length === 0
  const lossReasonError = errors.reasonId?.message ?? (hasReasonsError ? T.LOSS_REASONS_LOAD_ERROR : undefined)
  const lossReasonHelperText = lossReasonError ?? (hasNoReasonToPick ? T.NO_LOSS_REASONS_SET_UP : undefined)
  const lossCompetitorError = errors.competitorId?.message ?? (hasCompetitorsError ? T.COMPETITORS_LOAD_ERROR : undefined)

  useEffect(() => {
    if (!isOpen) reset(INITIAL_VALUES)
  }, [isOpen, reset])

  // A competitor only means something under the reason it was picked for.
  const handleReasonChange = useCallback(() => {
    clearErrors('reasonId')
    resetField('competitorId')
  }, [clearErrors, resetField])
  const handleCompetitorChange = useCallback(() => clearErrors('competitorId'), [clearErrors])

  const handleConfirm = useMemo(
    () =>
      handleSubmit(async values => {
        try {
          await onConfirm({
            ...(isCaptureEnabled && values.reasonId && { lossReasonId: values.reasonId }),
            ...(isCaptureEnabled && showCompetitor && values.competitorId && { lossCompetitorId: values.competitorId }),
            ...(values.notes.trim() && { notes: values.notes.trim() }),
          })
        } catch (error) {
          const { exceptionType, message } = (error as ServerError)?.data ?? {}
          // A competitor the dialog is not showing cannot carry the mark, so that refusal goes to a toast.
          if (exceptionType === 'lossReasonId' && isCaptureEnabled) {
            setError('reasonId', { type: 'server', message })
            return
          }
          if (exceptionType === 'lossCompetitorId' && showCompetitor) {
            setError('competitorId', { type: 'server', message })
            return
          }
          handleErrorWithFetchError(error, T.ACCOUNT_DEACTIVATION_FAILED)
        }
      }),
    [handleSubmit, onConfirm, isCaptureEnabled, showCompetitor, setError]
  )

  return (
    <HHBaseDialog fullWidth maxWidth="sm" open={isOpen && !isHidden} onClose={onClose}>
      <HHDialogTitle title={T.DEACTIVATE_ACCOUNT_DIALOG} onClose={onClose} />
      <DialogContent>
        <Stack gap={2.5} pt={1}>
          {isCaptureEnabled && hasSettingsError && <HHAlert severity="error">{T.ACCOUNT_SETTINGS_LOAD_ERROR}</HHAlert>}
          <HHAlert severity="warning">
            {T.DEACTIVATE_ACCOUNT_CAPTURE_LEAD}
            <strong>{accountName}</strong>
            {accountNumber && ` (${accountNumber})`}
            {isCaptureEnabled ? T.DEACTIVATE_ACCOUNT_CAPTURE_TAIL : T.DEACTIVATE_ACCOUNT_TAIL}
            {runsInBackground && ` ${T.DEACTIVATION_RUNS_IN_BACKGROUND}`}
          </HHAlert>
          <LifecycleCaptureFields
            competitorError={lossCompetitorError}
            competitorOptions={competitorOptions}
            control={control}
            isReasonCaptured={isCaptureEnabled}
            isReasonRequired={requireLossReason}
            reasonError={lossReasonError}
            reasonHelperText={lossReasonHelperText}
            reasonLabel={T.LOSS_REASON}
            reasonOptions={reasonOptions}
            showCompetitor={showCompetitor}
            onCompetitorChange={handleCompetitorChange}
            onReasonChange={handleReasonChange}
          />
        </Stack>
      </DialogContent>
      <HHDialogActions>
        <Button size="small" onClick={onClose}>
          {T.DISCARD}
        </Button>
        <Button disabled={!canConfirm} loading={isLoading} size="small" variant="contained" onClick={handleConfirm}>
          {T.DEACTIVATE_ACCOUNT_DIALOG}
        </Button>
      </HHDialogActions>
    </HHBaseDialog>
  )
}

export default DeactivateAccountDialog
